import { Card, CompartmentCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { ListConstructor } from "@/src/components/fetchableLists";
import { Exam, Patient, Reference } from "@/src/data/allTypes";
import { allItemTypes } from "@/src/data/data";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserId, getUserShortName, uid } from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { FaCircleCheck, FaRegCircle } from "react-icons/fa6";

import { notifySubscribers } from "../notifications/notificationsHook";
import { getStandardConfidentiality } from "./confidentialitiesHook";
import { openExamActionsDialog } from "../exams/examActionsDialog";
import { ExamDemandButton } from "../exams/examDemandButton";
import ExamSamplesButton from "../exams/examSamplesButton";
import { ExamsChoiceList } from "../exams/examsChoiceList";
import { openExamsVisualisationDrawer } from "../exams/openExamVisualisationDrawer";

export function ExamsHistory({
  title,
  patient,
  references,
}: {
  title?: string | null | undefined;
  patient: Patient;
  references?: (Reference | null | undefined)[] | null | undefined;
}) {
  const [reload, setReload] = useState(0);
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      "ExamsHistory",
      patient.id,
      reload,
      title,
      JSON.stringify(references),
    ],
    queryFn: () =>
      apiFetch("exams", "loadMany", {
        patientId: patient.id,
        referencesIds: references?.map(
          (reference) => getItemId(reference) || ""
        ),
      }),
  });

  return (
    <CompartmentCard
      title={title || "exams"}
      color="orange"
      onClick={async () => {
        await openDrawer({
          id: "examChoiceDialog",
          title: "Choisir un examen",
          content: (
            <ExamsChoiceList
              defaultReferences={references}
              onSelect={async (references) => {
                for (let reference of references) {
                  if (!reference) continue;
                  let exam: Exam = {
                    type: "exam",
                    examGroup: null,
                    creator: getUserId(),
                    patient: patient,
                    uploads: [],
                    parent: null,
                    notes: "",
                    conclusion: "",
                    status: "pending",
                    reference,
                    confidentiality: getStandardConfidentiality(patient),
                    readBy: [getUserId()],

                    content: {
                      patient: patient,
                      reference,
                      fieldAnswers: [],
                      creator: getUserId(),
                    },
                  };

                  let result = await apiFetch("exams", "addOne", {
                    exam,
                  });
                  if (!result) return;
                  exam = result;
                  closeDrawer("examChoiceDialog");
                  notifySubscribers({ itemId: result.id });
                  setReload((prev) => prev + 1);
                }
              }}
            />
          ),
          footer: <div></div>,
        });
      }}
    >
      <ListConstructor
        data={data?.exams}
        isError={isError}
        isLoading={isLoading}
        error={error}
      >
        <div className="grid c1 gap10">
          {data?.exams?.map((exam, index) => {
            const itemType = allItemTypes["exam"];
            return (
              <Card
                key={uid()}
                color={exam.status != "finished" ? "simple" : "orange"}
                title={exam.reference?.name}
                leading={
                  exam.status == "finished" ? (
                    <FaCircleCheck
                      size={18}
                      style={{ color: itemType.color }}
                    />
                  ) : (
                    <FaRegCircle size={18} style={{ color: itemType.color }} />
                  )
                }
                subTitle={getUserShortName(exam?.creator)}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (exam.status == "finished")
                    return await openExamsVisualisationDrawer({
                      exams: data?.exams || [],
                      index,
                      onEdit: async () => {
                        setReload(reload + 1);
                      },
                    });

                  await openExamActionsDialog({
                    allAppointments: data?.appointments || [],
                    exams: data?.exams || [],
                    index,
                    patient,
                    services: data?.services || [],
                    onChange: async () => {
                      setReload(reload + 1);
                    },
                  });
                }}
                onMore={async () => {
                  await openExamActionsDialog({
                    allAppointments: data?.appointments || [],
                    exams: data?.exams || [],
                    index,
                    patient,
                    services: data?.services || [],
                    onChange: async () => {
                      setReload(reload + 1);
                    },
                  });
                }}
              >
                <div className="c1 gap10">
                  <div className="gray">
                    {moment(exam?._date).format("LL HH:mm")}
                  </div>
                  <ExamDemandButton
                    patient={patient}
                    exam={exam}
                    isHospitalized={true}
                    allAppointments={data?.appointments || []}
                    allServices={data?.services || []}
                    onChange={async () => {
                      setReload(reload + 1);
                    }}
                  ></ExamDemandButton>
                  <ExamSamplesButton
                    patient={patient}
                    exam={exam}
                    onChange={async () => {
                      setReload(reload + 1);
                    }}
                  ></ExamSamplesButton>
                </div>
              </Card>
            );
          })}
        </div>
      </ListConstructor>
    </CompartmentCard>
  );
}
